import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, Navigate } from 'react-router-dom';
import { useCamp } from '../contexts/CampContext';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, Shield, CalendarCheck, Trophy,
  Calendar, Moon, BookOpen, MessageCircle, UserCheck,
  CheckSquare, List, FolderOpen, UtensilsCrossed, Wallet,
  Package, ClipboardList, Settings, ChevronLeft, ChevronRight,
  LogOut, ExternalLink, Menu, X, ChevronDown, AlertTriangle, History,
  Download, BarChart2, Newspaper, Eye, Globe, UserCircle, Bell, BellRing, Mail,
} from 'lucide-react';
import './CampInternalLayout.css';
import { APP_VERSION } from '../version';
import { useT } from '../i18n/index';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ProfileModal from '../components/ProfileModal';
import { usePush } from '../utils/usePush';

// MODULE_NAV uses translation keys for labels — resolved at render time via useT()
const MODULE_NAV = [
  { key: 'dashboard',     path: 'dashboard',  icon: LayoutDashboard, labelKey: 'nav.dashboard',      alwaysShow: true },
  { key: 'divider_camp',  labelKey: 'nav.section.camp',   isDividerLabel: true },
  { key: 'news',          path: 'neuigkeiten', icon: Newspaper,       labelKey: 'nav.news',           permissionKey: 'news.read' },
  { key: 'participants',  path: 'teilnehmer',  icon: Users,           labelKey: 'nav.participants',   permissionKey: 'participants.read' },
  { key: 'teams',         path: 'teams',       icon: Shield,          labelKey: 'nav.teams',          permissionKey: 'teams.read' },
  { key: 'attendance',    path: 'anwesenheit', icon: CalendarCheck,   labelKey: 'nav.attendance',     permissionKey: 'attendance.read' },
  { key: 'checkin',       path: 'checkin',     icon: CheckSquare,     labelKey: 'nav.checkin',        permissionKey: 'checkin.read' },
  { key: 'tournament',    path: 'turnier',     icon: Trophy,          labelKey: 'nav.tournament',     permissionKey: 'tournament.read' },
  { key: 'divider_prog',  labelKey: 'nav.section.program', isDividerLabel: true },
  { key: 'schedule',      path: 'plan',        icon: Calendar,        labelKey: 'nav.schedule',       permissionKey: 'schedule.read' },
  { key: 'evenings',      path: 'abende',      icon: Moon,            labelKey: 'nav.evenings',       permissionKey: 'evenings.read' },
  { key: 'teamtimes',     path: 'teamtimes',   icon: BookOpen,        labelKey: 'nav.teamtimes',      permissionKey: 'teamtimes.read' },
  { key: 'questions',     path: 'frage',       icon: MessageCircle,   labelKey: 'nav.questions',      permissionKey: 'questions.read' },
  { key: 'divider_org',   labelKey: 'nav.section.org',    isDividerLabel: true },
  { key: 'staff',         path: 'mitarbeiter', icon: UserCheck,       labelKey: 'nav.staff',          permissionKey: 'staff.read' },
  { key: 'todos',         path: 'todos',       icon: CheckSquare,     labelKey: 'nav.todos',          permissionKey: 'todos.read' },
  { key: 'lists',         path: 'listen',      icon: List,            labelKey: 'nav.lists',          permissionKey: 'lists.read' },
  { key: 'files',         path: 'dateien',     icon: FolderOpen,      labelKey: 'nav.files',          permissionKey: 'files.read' },
  { key: 'meals',         path: 'kueche',      icon: UtensilsCrossed, labelKey: 'nav.meals',          permissionKey: 'meals.read' },
  { key: 'finance',       path: 'finanzen',    icon: Wallet,          labelKey: 'nav.finance',        permissionKey: 'finance.read' },
  { key: 'equipment',     path: 'equipment',   icon: Package,         labelKey: 'nav.equipment',      permissionKey: 'equipment.read' },
  { key: 'flyer',         path: 'downloads',   icon: Download,        labelKey: 'nav.downloads',      permissionKey: 'flyer.read' },
  { key: 'divider_admin', labelKey: 'nav.section.admin',  isDividerLabel: true },
  { key: 'statistics',    path: 'statistiken', icon: BarChart2,       labelKey: 'nav.statistics',     permissionKey: 'stats.read', alwaysShow: true },
  { key: 'registration',  path: 'formulare',   icon: ClipboardList,   labelKey: 'nav.forms',          permissionKey: 'registration.read', role: 'admin' },
  { key: 'emails',        path: 'emails',      icon: Mail,            labelKey: 'nav.emails',         alwaysShow: true,                   role: 'admin' },
  { key: 'admin_camp',    path: 'admin',       icon: Settings,        labelKey: 'nav.camp_settings',  permissionKey: 'admin.settings',    role: 'admin' },
];

export default function CampInternalLayout() {
  const { camp, loading, error, isModuleEnabled, campSlug, allCampsInTenant, isActiveCamp, hasPermission: campHasPermission } = useCamp();
  const { user, logout, loading: authLoading, simulatedUser, exitSimulation, isSimulating } = useAuth();
  const t = useT();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });
  const [yearOpen, setYearOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const yearRef = useRef(null);
  const profileRef = useRef(null);
  const pushRef = useRef(null);
  const navigate = useNavigate();
  const { supported, permission, subscribed, loading: pushLoading, requestAndSubscribe, unsubscribe } = usePush();

  // Close year dropdown on outside click
  useEffect(() => {
    if (!yearOpen) return;
    const handler = (e) => { if (yearRef.current && !yearRef.current.contains(e.target)) setYearOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [yearOpen]);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!profileOpen) return;
    const handler = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileOpen]);

  // Close push dropdown on outside click
  useEffect(() => {
    if (!pushOpen) return;
    const handler = (e) => { if (pushRef.current && !pushRef.current.contains(e.target)) setPushOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pushOpen]);

  // PWA: inject intern-specific manifest so this camp's internal area installs
  // as its own separate app. scope is set to /:campSlug/intern/ by the backend,
  // keeping the installed app strictly within the internal area.
  useEffect(() => {
    if (!campSlug || !camp) return;

    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = `/${campSlug}/intern/manifest.json`;

    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!titleMeta) { titleMeta = document.createElement('meta'); titleMeta.setAttribute('name', 'apple-mobile-web-app-title'); document.head.appendChild(titleMeta); }
    titleMeta.setAttribute('content', `${camp.name} Intern`);

    // Dynamically set apple-touch-icon to the camp logo so iOS uses it as PWA icon
    const logoUrl = camp.branding?.logo_url;
    let touchIcon = document.querySelector('link[rel="apple-touch-icon"][data-camp]');
    if (logoUrl) {
      if (!touchIcon) {
        touchIcon = document.createElement('link');
        touchIcon.rel = 'apple-touch-icon';
        touchIcon.setAttribute('data-camp', '1');
        document.head.appendChild(touchIcon);
      }
      touchIcon.href = logoUrl;
    } else if (touchIcon) {
      touchIcon.remove();
    }

    return () => {
      if (link) link.href = '/manifest.json';
      document.querySelector('link[rel="apple-touch-icon"][data-camp]')?.remove();
    };
  }, [campSlug, camp]);

  const toggleCollapse = () => {
    setCollapsed(v => {
      try { localStorage.setItem('sidebarCollapsed', String(!v)); } catch {}
      return !v;
    });
  };

  // Wait for both auth and camp to finish loading before making any decision
  if (authLoading || loading) return <div className="internal-loading"><div className="spinner" /></div>;

  // Not authenticated → redirect to camp login, preserving the intended URL
  if (!user) return <Navigate to={`/${campSlug}/login`} state={{ from: window.location.pathname }} replace />;

  if (error) return (
    <div className="internal-error">
      <h2>{t('common.error')}</h2><p>{error}</p>
      <Link to="/" className="btn btn-primary">{t('common.to_home')}</Link>
    </div>
  );

  const userRoles = Array.isArray(user?.roles)
    ? user.roles
    : (typeof user?.roles === 'string' ? user.roles.split(',').map(r => r.trim()) : []);
  const isAdmin = userRoles.includes('admin') || user?.isSuperadmin;

  const canSeeAdminRoutes = isSimulating
    ? campHasPermission('admin.settings')
    : (isAdmin || campHasPermission('admin.settings'));

  const rawNavItems = MODULE_NAV.filter(item => {
    if (item.isDividerLabel) return true;
    if (item.role === 'admin' && !canSeeAdminRoutes) return false;
    if (item.permissionKey) return campHasPermission(item.permissionKey);
    if (item.alwaysShow) return true;
    return isModuleEnabled(item.key);
  });

  // Remove dividers whose section has no visible items
  const navItems = rawNavItems.filter((item, idx) => {
    if (!item.isDividerLabel) return true;
    const nextDivIdx = rawNavItems.findIndex((it, i) => i > idx && it.isDividerLabel);
    const section = rawNavItems.slice(idx + 1, nextDivIdx === -1 ? rawNavItems.length : nextDivIdx);
    return section.some(it => !it.isDividerLabel);
  });

  const displayUser = isSimulating ? simulatedUser : user;
  const initials = (displayUser?.display_name || displayUser?.username || 'U').charAt(0).toUpperCase();
  const displayRole = isSimulating
    ? (simulatedUser?.roles?.[0] || 'viewer')
    : (userRoles[0] || 'viewer');

  return (
    <div className={
      'camp-internal-layout' +
      (collapsed ? ' sidebar-collapsed' : '') +
      (sidebarOpen ? ' sidebar-open' : '')
    }>
      {/* Mobile backdrop */}
      <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          {camp?.branding?.logo_url && !collapsed && (
            <div style={{ padding: '10px 14px 0', display: 'flex', justifyContent: 'center' }}>
              <img
                src={camp.branding.logo_url}
                alt={camp.name}
                style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }}
              />
            </div>
          )}
          {allCampsInTenant.length > 1 && !collapsed ? (
            <div className="sidebar-year-switcher" ref={yearRef}>
              <button className="sidebar-year-btn" onClick={() => setYearOpen(v => !v)}>
                <History size={13} />
                <span className="sidebar-camp-name" style={{ flex: 1 }}>{camp?.name}</span>
                <ChevronDown size={12} style={{ transform: yearOpen ? 'rotate(180deg)' : 'none', transition: '0.15s' }} />
              </button>
              {yearOpen && (
                <div className="sidebar-year-dropdown">
                  {allCampsInTenant.map(c => (
                    <button
                      key={c.id}
                      className={`sidebar-year-option${c.slug === campSlug ? ' active' : ''}`}
                      onClick={() => { setYearOpen(false); navigate(`/${c.slug}/intern/dashboard`); }}
                    >
                      <span style={{ flex: 1 }}>{c.name}{c.year ? ` (${c.year})` : ''}</span>
                      {c.is_active ? (
                        <span className="year-badge year-badge--active">{t('layout.active')}</span>
                      ) : (
                        <span className="year-badge year-badge--archive">{t('layout.archive')}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="sidebar-camp-name">{camp?.name}</span>
          )}
          <button className="sidebar-toggle" onClick={toggleCollapse} title={collapsed ? t('nav.expand') : t('nav.collapse')}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => {
            if (item.isDividerLabel) {
              if (collapsed) return <div key={item.key} className="sidebar-divider" />;
              return <div key={item.key} className="sidebar-section-label">{t(item.labelKey)}</div>;
            }
            const Icon = item.icon;
            const label = t(item.labelKey);
            return (
              <NavLink
                key={item.key}
                to={`/${campSlug}/intern/${item.path}`}
                className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
                title={collapsed ? label : undefined}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sidebar-icon">
                  {Icon && <Icon size={16} strokeWidth={1.75} />}
                </span>
                <span className="sidebar-label">{label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className={`sidebar-user${isSimulating ? ' sidebar-user--simulating' : ''}`}>
            <div className="user-avatar-initials">{initials}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{displayUser?.display_name || displayUser?.username}</span>
              <span className="sidebar-user-role">{t('role.' + displayRole)}</span>
            </div>
            {isSimulating && <Eye size={12} style={{ color: '#ffd60a', flexShrink: 0 }} />}
          </div>
          <div className="sidebar-version">v{APP_VERSION}</div>
        </div>
      </aside>

      {/* Main content */}
      <div className="sidebar-content">
        <header className="internal-header">
          <div className="internal-header-left">
            <button
              className="mobile-sidebar-toggle btn btn-ghost btn-sm"
              onClick={() => setSidebarOpen(v => !v)}
              aria-label={t('layout.menu')}
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <span className="internal-header-title">{camp?.name}</span>
          </div>
          <div className="internal-header-right">
            {/* Push notification bell */}
            {supported && permission !== 'denied' && (
              <div ref={pushRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setPushOpen(v => !v)}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                    background: pushOpen ? 'var(--color-surface-2, rgba(255,255,255,0.08))' : 'transparent',
                    border: '1px solid',
                    borderColor: subscribed ? 'rgba(255,214,10,0.35)' : 'transparent',
                    transition: 'all 150ms',
                  }}
                  aria-label={t('push.title')}
                >
                  {subscribed
                    ? <BellRing size={16} style={{ color: 'var(--color-accent)' }} />
                    : <Bell size={16} style={{ color: 'var(--color-muted)' }} />
                  }
                  {!subscribed && (
                    <span style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 6, height: 6, borderRadius: '50%',
                      background: 'var(--color-primary)',
                      border: '1.5px solid var(--color-bg)',
                    }} />
                  )}
                </button>

                {pushOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                    width: 256, zIndex: 300,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderTop: '2px solid var(--color-accent)',
                    borderRadius: '0 0 10px 10px',
                    boxShadow: '0 16px 48px rgba(0,0,0,.5)',
                    overflow: 'hidden',
                  }}>
                    {subscribed ? (
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: 'rgba(255,214,10,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <BellRing size={15} style={{ color: 'var(--color-accent)' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                              {t('push.active')}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#5be0a0', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5be0a0', display: 'inline-block' }} />
                              Live
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={async () => { await unsubscribe(); setPushOpen(false); }}
                          style={{
                            width: '100%', padding: '7px', borderRadius: 6, cursor: 'pointer',
                            background: 'transparent', border: '1px solid var(--color-border)',
                            color: 'var(--color-muted)', fontSize: '0.78rem', fontWeight: 600,
                            transition: 'all 150ms',
                          }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = '#e63946'; e.currentTarget.style.color = '#e63946'; }}
                          onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-muted)'; }}
                        >
                          {t('push.disable')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: 'rgba(230,57,70,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Bell size={15} style={{ color: 'var(--color-primary)' }} />
                          </div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            {t('push.title')}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginBottom: 12, lineHeight: 1.55 }}>
                          {t('push.desc')}
                        </div>
                        <button
                          disabled={pushLoading}
                          onClick={async () => { const ok = await requestAndSubscribe(); if (ok) setPushOpen(false); }}
                          style={{
                            width: '100%', padding: '8px', borderRadius: 6, cursor: 'pointer',
                            background: 'var(--color-primary)', border: 'none',
                            color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                            fontFamily: 'Oswald, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            opacity: pushLoading ? 0.6 : 1,
                          }}
                        >
                          {pushLoading
                            ? <span className="spinner" style={{ width: 12, height: 12 }} />
                            : <><Bell size={12} /> {t('push.enable')}</>
                          }
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="profile-menu-wrap" ref={profileRef}>
              <button
                className={`profile-avatar-btn${profileOpen ? ' active' : ''}`}
                onClick={() => setProfileOpen(v => !v)}
                aria-label="Profile menu"
              >
                <div className="profile-avatar-ring">
                  <div className="user-avatar-initials profile-avatar-initials">{initials}</div>
                </div>
                <div className="profile-avatar-info hide-mobile">
                  <span className="profile-avatar-name">{displayUser?.display_name || displayUser?.username}</span>
                  <span className="profile-avatar-role">{t('role.' + displayRole)}</span>
                </div>
                <ChevronDown size={12} className={`profile-chevron hide-mobile${profileOpen ? ' open' : ''}`} />
              </button>

              {profileOpen && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-identity">
                    <div className="profile-dropdown-avatar">
                      <div className="user-avatar-initials" style={{ width: 44, height: 44, fontSize: '1.1rem' }}>{initials}</div>
                      {isSimulating && (
                        <span className="profile-sim-badge"><Eye size={10} /></span>
                      )}
                    </div>
                    <div className="profile-dropdown-info">
                      <span className="profile-dropdown-name">{displayUser?.display_name || displayUser?.username}</span>
                      <span className="profile-dropdown-role">{t('role.' + displayRole)}</span>
                    </div>
                  </div>

                  <div className="profile-dropdown-divider" />

                  <div className="profile-dropdown-lang">
                    <Globe size={13} className="profile-dropdown-lang-icon" />
                    <LanguageSwitcher variant="pills" />
                  </div>

                  <div className="profile-dropdown-divider" />

                  <button
                    className="profile-dropdown-item"
                    onClick={() => { setProfileOpen(false); setProfileModalOpen(true); }}
                  >
                    <UserCircle size={15} strokeWidth={1.75} />
                    <span>{t('profile.title')}</span>
                  </button>

                  <Link
                    to={`/${campSlug}`}
                    className="profile-dropdown-item"
                    onClick={() => setProfileOpen(false)}
                    target="_blank"
                  >
                    <ExternalLink size={15} strokeWidth={1.75} />
                    <span>{t('nav.to_public')}</span>
                  </Link>

                  <button
                    className="profile-dropdown-item profile-dropdown-item--danger"
                    onClick={() => { setProfileOpen(false); logout(); navigate(`/${campSlug}/login`); }}
                  >
                    <LogOut size={15} strokeWidth={1.75} />
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {!isActiveCamp && (
          <div className="archive-banner">
            <AlertTriangle size={14} />
            <span>{t('layout.archive_banner', { name: camp?.name })}</span>
            {allCampsInTenant.length > 0 && (() => {
              const active = allCampsInTenant.find(c => c.is_active);
              return active ? (
                <button className="archive-banner-btn" onClick={() => navigate(`/${active.slug}/intern/dashboard`)}>
                  {t('layout.to_active_camp')}
                </button>
              ) : null;
            })()}
          </div>
        )}
        {isSimulating && (
          <div className="simulation-banner">
            <Eye size={14} />
            <span>{t('layout.simulation_banner', { name: simulatedUser?.display_name || simulatedUser?.username })}</span>
            <span className="simulation-roles">{simulatedUser?.roles?.join(', ')}</span>
            <button className="btn btn-sm simulation-exit-btn" onClick={exitSimulation}>{t('layout.end_simulation')}</button>
          </div>
        )}
        <main className="internal-main">
          <Outlet />
        </main>
      </div>

      {profileModalOpen && (
        <ProfileModal
          campSlug={campSlug}
          onClose={() => setProfileModalOpen(false)}
        />
      )}
    </div>
  );
}
